import { useMemo, useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { useFeaturesCatalog } from '@/hooks/useFeatures';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { Feature } from '@/types/features';

type EntitlementOverrideEditorProps = {
  featureOverrides: Record<string, unknown>;
  limitOverrides: Record<string, number | null>;
  onFeatureOverridesChange: (overrides: Record<string, unknown>) => void;
  onLimitOverridesChange: (overrides: Record<string, number | null>) => void;
};

const SUGGESTED_LIMITS = [
  'documents_per_month',
  'users',
  'expense_users',
  'mailboxes',
  'llm_chat_budget_cents_per_month',
  'overage_per_document',
] as const;

const FEATURE_OVERRIDE_OPTIONS = [
  { id: 'default', label: 'Plan default' },
  { id: 'enabled', label: 'Enabled' },
  { id: 'disabled', label: 'Disabled' },
];

function humanizeKey(key: string) {
  return key
    .replace(/^llm_/, 'AI ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatCategory(category: string | null | undefined) {
  return category ? humanizeKey(category) : 'Uncategorized';
}

function getFeatureState(value: unknown) {
  if (value === true) {
    return 'enabled';
  }
  if (value === false) {
    return 'disabled';
  }
  if (value === undefined) {
    return 'default';
  }
  return 'custom';
}

function sortFeatures(features: Feature[]) {
  return [...features].sort((first, second) => {
    const categoryCompare = formatCategory(first.category).localeCompare(formatCategory(second.category));
    if (categoryCompare !== 0) {
      return categoryCompare;
    }
    if (first.sort_order !== second.sort_order) {
      return first.sort_order - second.sort_order;
    }
    return first.display_name.localeCompare(second.display_name);
  });
}

export function EntitlementOverrideEditor({
  featureOverrides,
  limitOverrides,
  onFeatureOverridesChange,
  onLimitOverridesChange,
}: EntitlementOverrideEditorProps) {
  const features = useFeaturesCatalog();
  const [featureSearch, setFeatureSearch] = useState('');
  const [limitKeyDrafts, setLimitKeyDrafts] = useState<Record<string, string>>({});

  const featureRows = useMemo(() => {
    const catalogByKey = new Map((features.data ?? []).map((feature) => [feature.key, feature]));
    const customRows: Feature[] = Object.keys(featureOverrides)
      .filter((key) => !catalogByKey.has(key))
      .map((key) => ({
        key,
        display_name: humanizeKey(key),
        description: null,
        category: 'custom',
        sort_order: 9999,
        is_active: true,
        created_at: '',
        updated_at: null,
      }));

    return sortFeatures([...(features.data ?? []), ...customRows]).filter((feature) => {
      const query = featureSearch.trim().toLowerCase();
      if (!query) {
        return true;
      }
      return (
        feature.key.toLowerCase().includes(query) ||
        feature.display_name.toLowerCase().includes(query) ||
        (feature.description ?? '').toLowerCase().includes(query)
      );
    });
  }, [featureOverrides, featureSearch, features.data]);

  function updateFeatureOverride(key: string, value: 'default' | 'enabled' | 'disabled') {
    const next = { ...featureOverrides };
    if (value === 'default') {
      delete next[key];
    } else {
      next[key] = value === 'enabled';
    }
    onFeatureOverridesChange(next);
  }

  function addLimitOverride() {
    const nextKey = SUGGESTED_LIMITS.find((key) => !(key in limitOverrides)) ?? `custom_limit_${Object.keys(limitOverrides).length + 1}`;
    onLimitOverridesChange({ ...limitOverrides, [nextKey]: null });
  }

  function updateLimitKey(previousKey: string, nextKey: string) {
    const normalizedKey = nextKey.trim();
    if (!normalizedKey || normalizedKey === previousKey) {
      setLimitKeyDrafts((current) => {
        const next = { ...current };
        delete next[previousKey];
        return next;
      });
      return;
    }
    const next = { ...limitOverrides };
    const value = next[previousKey];
    delete next[previousKey];
    next[normalizedKey] = value;
    onLimitOverridesChange(next);
    setLimitKeyDrafts((current) => {
      const updated = { ...current };
      delete updated[previousKey];
      return updated;
    });
  }

  function updateLimitValue(key: string, value: number | null) {
    onLimitOverridesChange({ ...limitOverrides, [key]: value });
  }

  function removeLimitOverride(key: string) {
    const next = { ...limitOverrides };
    delete next[key];
    onLimitOverridesChange(next);
    setLimitKeyDrafts((current) => {
      const updated = { ...current };
      delete updated[key];
      return updated;
    });
  }

  return (
    <div className="override-editor">
      <section className="override-panel">
        <div className="override-panel-header">
          <div>
            <h4>Feature Overrides</h4>
            <span>{Object.keys(featureOverrides).length} selected</span>
          </div>
          <div className="search-field override-search">
            <Search size={16} className="text-content-muted" />
            <input
              type="text"
              placeholder="Search features"
              value={featureSearch}
              onChange={(event) => setFeatureSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="override-feature-list">
          {features.isLoading ? (
            <div className="loading-row">Loading feature catalog...</div>
          ) : featureRows.length === 0 ? (
            <div className="loading-row">No features found.</div>
          ) : (
            featureRows.map((feature) => {
              const value = featureOverrides[feature.key];
              const state = getFeatureState(value);
              return (
                <div className="override-feature-row" key={feature.key}>
                  <div className="override-feature-copy">
                    <strong>{feature.display_name}</strong>
                    <span>{formatCategory(feature.category)}</span>
                    <code>{feature.key}</code>
                  </div>
                  <SearchableSelect
                    options={state === 'custom' ? [{ id: 'custom', label: 'Custom value' }, ...FEATURE_OVERRIDE_OPTIONS] : FEATURE_OVERRIDE_OPTIONS}
                    value={state}
                    onChange={(value) => {
                      if (value !== 'custom') {
                        updateFeatureOverride(
                          feature.key,
                          value as 'default' | 'enabled' | 'disabled',
                        );
                      }
                    }}
                    placeholder="Feature state"
                    clearable={false}
                  />
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="override-panel">
        <div className="override-panel-header">
          <div>
            <h4>Limit Overrides</h4>
            <span>{Object.keys(limitOverrides).length} selected</span>
          </div>
          <button type="button" className="button button-secondary table-link-button" onClick={addLimitOverride}>
            <Plus size={14} />
            Add limit
          </button>
        </div>

        <datalist id="subscription-limit-keys">
          {SUGGESTED_LIMITS.map((key) => (
            <option key={key} value={key}>
              {humanizeKey(key)}
            </option>
          ))}
        </datalist>

        <div className="override-limit-list">
          {Object.keys(limitOverrides).length === 0 ? (
            <div className="loading-row">No limit overrides selected.</div>
          ) : (
            Object.entries(limitOverrides).map(([key, value]) => (
              <div className="override-limit-row" key={key}>
                <label className="form-field">
                  <span>Limit</span>
                  <input
                    list="subscription-limit-keys"
                    value={limitKeyDrafts[key] ?? key}
                    onBlur={(event) => updateLimitKey(key, event.target.value)}
                    onChange={(event) =>
                      setLimitKeyDrafts((current) => ({ ...current, [key]: event.target.value }))
                    }
                  />
                </label>
                <label className="form-field">
                  <span>Value</span>
                  <input
                    type="number"
                    step="any"
                    value={value ?? ''}
                    disabled={value === -1}
                    onChange={(event) =>
                      updateLimitValue(key, event.target.value === '' ? null : Number(event.target.value))
                    }
                  />
                </label>
                <label className="override-unlimited-toggle">
                  <input
                    type="checkbox"
                    checked={value === -1}
                    onChange={(event) => updateLimitValue(key, event.target.checked ? -1 : null)}
                  />
                  <span>Unlimited</span>
                </label>
                <button
                  type="button"
                  className="button button-secondary override-remove-button"
                  onClick={() => removeLimitOverride(key)}
                  aria-label={`Remove ${humanizeKey(key)} override`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
